import React from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard } from "@narmi/design_system";
import type { ReactLocalization } from "@fluent/react";
import type { Features } from "byzantine/src/Feature";
import type { Role } from "byzantine/src/Role";
import { MODEL_STR } from "byzantine/src/Role";
import { getRoleLevelModelStrs } from "./RoleDisplayUtils";
import styles from "./OrganizationRoles.module.scss";

// Fluent ids stay literal so they can be statically checked.
const LABELS: Record<string, (l10n: ReactLocalization) => string> = {
  [MODEL_STR.RECIPIENT]: (l10n) => l10n.getString("manage-users-recipient"),
  [MODEL_STR.CARD]: (l10n) => l10n.getString("manage-users-card"),
  [MODEL_STR.PAYMENT]: (l10n) => l10n.getString("org-role-new-perm-billpay"),
  [MODEL_STR.PAYEE]: (l10n) =>
    l10n.getString("org-role-new-perm-billpay-recipients"),
  [MODEL_STR.EXTERNAL_ACCOUNT]: (l10n) =>
    l10n.getString("manage-users-external-account"),
  [MODEL_STR.ZELLE]: (l10n) => l10n.getString("manage-users-zelle"),
  [MODEL_STR.DOCUMENTS]: (l10n) => l10n.getString("manage-users-documents"),
  [MODEL_STR.ACH_IMPORT_FILE_DOWNLOADS]: (l10n) =>
    l10n.getString("manage-users-ach-import-file-downloads"),
  [MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES]: (l10n) =>
    l10n.getString("org-role-perm-create-api-tokens"),
  [MODEL_STR.DUAL_APPROVAL_REQUEST]: (l10n) =>
    l10n.getString("manage-users-dual-approval-request"),
  [MODEL_STR.APPROVE_ACH_TEMPLATES]: (l10n) =>
    l10n.getString("manage-users-ach-instant-approve-templates"),
};

interface RoleLevelPermissionsViewProps {
  role: Role;
  /** Institution features: permissions it doesn't offer are left out (Admin holds them all). */
  features?: Features;
  /** "page" separates sections with dividers; "card" puts each checklist in a bordered card. */
  variant?: "page" | "card";
}

/** Role-level permissions and approvals as two checklists. Shared across role surfaces. */
export default function RoleLevelPermissionsView({
  role,
  features,
  variant = "page",
}: RoleLevelPermissionsViewProps) {
  const { l10n } = useLocalization();
  const { permissions, approvals } = getRoleLevelModelStrs(role, features);

  const renderRow = (modelStr: string) => (
    <div
      key={modelStr}
      className={`${styles.permissionItem} ${styles.permissionsRow}`}
    >
      <span
        className={`narmi-icon-check ${styles.accountsIcon} ${styles.permissionCheck}`}
        aria-hidden="true"
      />
      <div>
        <p className={`fontSize--16px ${styles.permissionTitle}`}>
          {LABELS[modelStr](l10n)}
        </p>
      </div>
    </div>
  );

  const renderSection = (
    title: string,
    modelStrs: string[],
    emptyMessage: string
  ) => {
    const body =
      modelStrs.length > 0 ? (
        modelStrs.map(renderRow)
      ) : (
        <p className={styles.permissionSubtitle}>{emptyMessage}</p>
      );
    const header = (
      <div
        className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
      >
        {title}
      </div>
    );
    if (variant === "card") {
      return (
        <div className={styles.cardSection}>
          {header}
          <ContentCard kind="bordered" paddingSize="m">
            {body}
          </ContentCard>
        </div>
      );
    }
    return (
      <div className={styles.usersSection}>
        {header}
        {body}
      </div>
    );
  };

  return (
    <>
      {renderSection(
        l10n.getString("org-role-level-permissions"),
        permissions,
        l10n.getString("org-role-level-permissions-none")
      )}
      {variant === "page" && <hr className={styles.divider} />}
      {renderSection(
        l10n.getString("org-role-approvals"),
        approvals,
        l10n.getString("org-role-approvals-none")
      )}
    </>
  );
}
